import { useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SectionId } from '@/components/OnePager';
import { NavigationDrawer } from '@/components/NavigationDrawer';

interface OnePageHeaderProps {
  activeSection: SectionId;
  isMenuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onNavigate: (sectionId: SectionId) => void;
}

const sectionTitles: Record<SectionId, string> = {
  'drink-log': 'dashboardTitle',
  'rewards': 'rewardsTitle',
  'leaderboard': 'leaderboardTitle',
  'profile': 'profileTitle',
};

export function OnePageHeader({ activeSection, isMenuOpen, onMenuOpenChange, onNavigate }: OnePageHeaderProps) {
  const { t } = useLanguage();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  // Lock background scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    onMenuOpenChange(false);
    await signOut();
    navigate('/', { replace: true });
  };

  return (
    <>
      {/* Header */}
      <header className="p-4 border-b border-border sticky top-0 bg-background z-40">
        <div className="flex items-center justify-between max-w-lg mx-auto px-0">
          <div className="w-9" /> {/* Spacer for balance */}
          <h1 className="text-xl font-serif font-bold">{t(sectionTitles[activeSection])}</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMenuOpenChange(!isMenuOpen)}
            className="p-2 bg-primary/10 hover:bg-primary/20 rounded-full"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5 text-primary" />
            ) : (
              <Menu className="h-5 w-5 text-primary" />
            )}
          </Button>
        </div>
      </header>

      {/* Navigation Drawer */}
      <NavigationDrawer
        isOpen={isMenuOpen}
        onClose={() => onMenuOpenChange(false)}
        isAuthenticated={true}
        activeSection={activeSection}
        onNavigate={onNavigate}
        onSignOut={handleSignOut}
      />
    </>
  );
}
